// Measures where a text range sits inside a <textarea> by rendering the same
// text into an invisible "mirror" div with identical typography. Textareas do
// not expose layout for their content, so this is the standard workaround.

const MIRRORED_PROPERTIES = [
  'direction',
  'boxSizing',
  'width',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'fontFeatureSettings',
  'textAlign',
  'textTransform',
  'textIndent',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
];

/**
 * Returns the bounding box of the characters between `start` and `end`,
 * relative to the textarea's top-left border edge (scroll offset applied), or
 * null when layout information is unavailable (e.g. jsdom).
 *
 * @returns {{top:number,bottom:number,left:number,right:number}|null}
 */
export function getTextareaSelectionRect(textarea, start, end) {
  if (!textarea || typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (!(end > start)) return null;

  const value = textarea.value || '';
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const style = mirror.style;

  MIRRORED_PROPERTIES.forEach((prop) => {
    style[prop] = computed[prop];
  });
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.pointerEvents = 'none';
  style.top = '0';
  style.left = '-99999px';
  style.overflow = 'hidden';
  style.height = 'auto';
  style.whiteSpace = 'pre-wrap';
  style.overflowWrap = 'break-word';
  style.wordBreak = computed.wordBreak;

  // A vertical scrollbar narrows the textarea's text area; mirror that.
  const borderX = (parseFloat(computed.borderLeftWidth) || 0) + (parseFloat(computed.borderRightWidth) || 0);
  const scrollbarWidth = textarea.offsetWidth - textarea.clientWidth - borderX;
  if (scrollbarWidth > 0) {
    style.paddingRight = `${(parseFloat(computed.paddingRight) || 0) + scrollbarWidth}px`;
  }

  mirror.appendChild(document.createTextNode(value.slice(0, start)));
  const marker = document.createElement('span');
  marker.textContent = value.slice(start, end);
  mirror.appendChild(marker);
  // The rest of the text keeps word-wrapping at the selection end accurate.
  mirror.appendChild(document.createTextNode(value.slice(end) || '.'));

  document.body.appendChild(mirror);
  try {
    const mirrorRect = mirror.getBoundingClientRect();
    const allRects = Array.from(marker.getClientRects ? marker.getClientRects() : []);
    const rects = allRects.filter((rect) => rect.width > 0).length > 0
      ? allRects.filter((rect) => rect.width > 0)
      : allRects;
    if (rects.length === 0) return null;

    const first = rects[0];
    const last = rects[rects.length - 1];
    const left = Math.min(...rects.map((rect) => rect.left));
    const right = Math.max(...rects.map((rect) => rect.right));
    if (![first.top, last.bottom, left, right].every(Number.isFinite)) return null;
    if (first.top === 0 && last.bottom === 0 && left === 0 && right === 0) return null;

    const offsetX = mirrorRect.left + textarea.scrollLeft;
    const offsetY = mirrorRect.top + textarea.scrollTop;
    return {
      top: first.top - offsetY,
      bottom: last.bottom - offsetY,
      left: left - offsetX,
      right: right - offsetX,
    };
  } finally {
    mirror.remove();
  }
}

export default getTextareaSelectionRect;
